import { useEffect, useState } from 'react'
import {
  Button, Space, Tag, message, Modal, Form, Input,
  InputNumber, Popconfirm, Tree, Card
} from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, FolderOutlined, FolderOpenOutlined, AppstoreOutlined } from '@ant-design/icons'
import { categoryApi } from '../services/api'

interface SubCategory {
  id: number
  name: string
  code: string
  sort_order: number
  status: number
  category_id: number
}

interface Category {
  id: number
  name: string
  code: string
  icon?: string
  sort_order: number
  status: number
  subCategories?: SubCategory[]
}

function Categories() {
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(false)
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([])

  // 大类弹窗
  const [catModalOpen, setCatModalOpen] = useState(false)
  const [editingCat, setEditingCat] = useState<Category | null>(null)
  const [catForm] = Form.useForm()

  // 小类弹窗
  const [subModalOpen, setSubModalOpen] = useState(false)
  const [editingSub, setEditingSub] = useState<SubCategory | null>(null)
  const [currentCatId, setCurrentCatId] = useState<number | null>(null)
  const [subForm] = Form.useForm()

  const fetchCategories = async () => {
    setLoading(true)
    try {
      const res: any = await categoryApi.getCategories()
      if (res.code === 200) {
        setCategories(res.data)
        // 默认展开所有大类
        setExpandedKeys(res.data.map((c: Category) => `cat-${c.id}`))
      }
    } catch {
      message.error('获取分类失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchCategories() }, [])

  // 大类操作
  const handleAddCat = () => {
    setEditingCat(null)
    catForm.resetFields()
    setCatModalOpen(true)
  }

  const handleEditCat = (cat: Category) => {
    setEditingCat(cat)
    catForm.setFieldsValue({ name: cat.name, code: cat.code, sort_order: cat.sort_order })
    setCatModalOpen(true)
  }

  const handleCatOk = async () => {
    try {
      const values = await catForm.validateFields()
      if (editingCat) {
        await categoryApi.updateCategory(editingCat.id, values)
        message.success('更新成功')
      } else {
        await categoryApi.createCategory(values)
        message.success('添加成功')
      }
      setCatModalOpen(false)
      fetchCategories()
    } catch (err: any) {
      if (err?.errorFields) return
      message.error('操作失败')
    }
  }

  const handleDeleteCat = async (id: number) => {
    try {
      await categoryApi.deleteCategory(id)
      message.success('删除成功')
      fetchCategories()
    } catch {
      message.error('删除失败，请先删除该分类下的商品')
    }
  }

  // 小类操作
  const handleAddSub = (catId: number) => {
    setEditingSub(null)
    setCurrentCatId(catId)
    subForm.resetFields()
    setSubModalOpen(true)
  }

  const handleEditSub = (sub: SubCategory) => {
    setEditingSub(sub)
    setCurrentCatId(sub.category_id)
    subForm.setFieldsValue({ name: sub.name, code: sub.code, sort_order: sub.sort_order })
    setSubModalOpen(true)
  }

  const handleSubOk = async () => {
    try {
      const values = await subForm.validateFields()
      if (editingSub) {
        await categoryApi.updateSubCategory(editingSub.id, values)
        message.success('更新成功')
      } else {
        await categoryApi.createSubCategory({ ...values, category_id: currentCatId })
        message.success('添加成功')
      }
      setSubModalOpen(false)
      fetchCategories()
    } catch (err: any) {
      if (err?.errorFields) return
      message.error('操作失败')
    }
  }

  const handleDeleteSub = async (id: number) => {
    try {
      await categoryApi.deleteSubCategory(id)
      message.success('删除成功')
      fetchCategories()
    } catch {
      message.error('删除失败，请先删除该小类下的商品')
    }
  }

  // 构建树形数据
  const treeData = categories.map(cat => ({
    key: `cat-${cat.id}`,
    title: (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', paddingRight: 8 }}>
        <Space>
          {cat.icon ? <img src={cat.icon} alt={cat.name} style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'cover' }} /> : <FolderOutlined />}
          <Tag color="purple">{cat.code}</Tag>
          <span style={{ fontWeight: 600, fontSize: 15 }}>{cat.name}</span>
          <Tag color="blue">{cat.subCategories?.length || 0} 个小类</Tag>
        </Space>
        <Space onClick={e => e.stopPropagation()}>
          <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => handleAddSub(cat.id)} style={{ backgroundColor: '#55266c' }}>
            添加小类
          </Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEditCat(cat)}>编辑</Button>
          <Popconfirm title="确认删除此大类？" onConfirm={() => handleDeleteCat(cat.id)} okText="确认" cancelText="取消">
            <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      </div>
    ),
    icon: <FolderOutlined />,
    children: (cat.subCategories || []).map(sub => ({
      key: `sub-${sub.id}`,
      title: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', paddingRight: 8 }}>
          <Space>
            <AppstoreOutlined style={{ color: '#999' }} />
            <span style={{ color: '#666' }}>{sub.code}</span>
            <span>{sub.name}</span>
            <Tag color={sub.status === 1 ? 'success' : 'default'}>{sub.status === 1 ? '启用' : '禁用'}</Tag>
          </Space>
          <Space onClick={e => e.stopPropagation()}>
            <Button size="small" type="link" icon={<EditOutlined />} onClick={() => handleEditSub(sub)}>编辑</Button>
            <Popconfirm title="确认删除此小类？" onConfirm={() => handleDeleteSub(sub.id)} okText="确认" cancelText="取消">
              <Button size="small" type="link" danger icon={<DeleteOutlined />}>删除</Button>
            </Popconfirm>
          </Space>
        </div>
      ),
      icon: <AppstoreOutlined />,
      isLeaf: true,
    }))
  }))

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <span style={{ fontSize: 16, fontWeight: 600 }}>
          共 {categories.length} 个大类，{categories.reduce((n, c) => n + (c.subCategories?.length || 0), 0)} 个小类
        </span>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddCat} style={{ backgroundColor: '#55266c' }}>
          添加大类
        </Button>
      </div>

      <Card bordered={false} loading={loading}>
        <Tree
          showIcon
          showLine={{ showLeafIcon: true }}
          expandedKeys={expandedKeys}
          onExpand={(keys) => setExpandedKeys(keys)}
          treeData={treeData}
          style={{ fontSize: 14 }}
          switcherIcon={<FolderOpenOutlined />}
        />
      </Card>

      {/* 大类弹窗 */}
      <Modal
        title={editingCat ? '编辑大类' : '添加大类'}
        open={catModalOpen}
        onOk={handleCatOk}
        onCancel={() => setCatModalOpen(false)}
        okText="保存"
        cancelText="取消"
        okButtonProps={{ style: { backgroundColor: '#55266c' } }}
      >
        <Form form={catForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="大类名称" rules={[{ required: true, message: '请输入大类名称' }]}>
            <Input placeholder="如: 新鲜蔬果" />
          </Form.Item>
          <Form.Item name="code" label="大类编码" rules={[{ required: true, message: '请输入编码' }]}>
            <Input placeholder="如: 01" />
          </Form.Item>
          <Form.Item name="sort_order" label="排序" initialValue={0}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 小类弹窗 */}
      <Modal
        title={editingSub ? '编辑小类' : '添加小类'}
        open={subModalOpen}
        onOk={handleSubOk}
        onCancel={() => setSubModalOpen(false)}
        okText="保存"
        cancelText="取消"
        okButtonProps={{ style: { backgroundColor: '#55266c' } }}
      >
        <Form form={subForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="小类名称" rules={[{ required: true, message: '请输入小类名称' }]}>
            <Input placeholder="如: 叶菜类" />
          </Form.Item>
          <Form.Item name="code" label="小类编码" rules={[{ required: true, message: '请输入编码' }]}>
            <Input placeholder="如: 01" />
          </Form.Item>
          <Form.Item name="sort_order" label="排序" initialValue={0}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default Categories
